/**
 * WorkspaceCurator — Modal overlay wrapping CuratorView for workspace use
 *
 * Enables curator as a workspace tool:
 * - Opens from toolbar button or "Find Related" action
 * - Results can be added directly to the card stack
 * - Pre-fills query from active card content or selected text
 * - Reopens saved curator searches from ProjectBrowser (M5)
 * - Left sidebar shows saved searches per project (Phase 3)
 *
 * @module @humanizer/studio/components/workspace/WorkspaceCurator
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { CuratorView, type CuratorSessionState, type FindBuffer } from '../archive/CuratorView';
import { useBufferSync, type ContentItem } from '../../contexts/BufferSyncContext';
import { useProject } from '../../contexts/ProjectContext';
import { useToast } from '../../contexts/ToastContext';
import { createLogger } from '../../utils/debug';

const log = createLogger('WorkspaceCurator');

interface SavedSearch {
  id: string;
  query: string;
  resultCount: number;
  anchor: string;
  timestamp: number;
}

interface WorkspaceCuratorProps {
  /** Close the modal */
  onClose: () => void;
  /** Optional initial query to pre-fill */
  initialQuery?: string | null;
  /** Reopen a saved curator search (M5) */
  initialFindBuffer?: FindBuffer;
}

export function WorkspaceCurator({ onClose, initialQuery, initialFindBuffer }: WorkspaceCuratorProps) {
  const { addToStack } = useBufferSync();
  const { activeProject } = useProject();
  const { addToast } = useToast();
  const sessionStateRef = useRef<CuratorSessionState | undefined>(undefined);

  // Saved searches sidebar
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [sidebarFindBuffer, setSidebarFindBuffer] = useState<FindBuffer | undefined>(initialFindBuffer);

  // Load saved searches when project changes
  useEffect(() => {
    if (!activeProject || !window.electronBuffer?.listByProject) return;

    window.electronBuffer.listByProject(activeProject.id)
      .then((buffers: Array<{ id: string; origin?: { metadata?: Record<string, unknown> }; createdAt?: number }>) => {
        const searches: SavedSearch[] = [];
        for (const buf of buffers) {
          const meta = buf.origin?.metadata;
          if (meta?.curatorSearch) {
            searches.push({
              id: buf.id,
              query: (meta.curatorQuery as string) ?? '',
              resultCount: (meta.resultCount as number) ?? 0,
              anchor: (meta.curatorAnchor as string) ?? '',
              timestamp: (meta.searchTimestamp as number) ?? buf.createdAt ?? 0,
            });
          }
        }
        // Sort newest first
        searches.sort((a, b) => b.timestamp - a.timestamp);
        setSavedSearches(searches);
      })
      .catch((err: unknown) => {
        log.warn('Failed to load saved searches:', err);
      });
  }, [activeProject]);

  // D2: Add curator result to stack as a new card
  const handleAddToStack = useCallback(async (nodeId: string, text: string, sourceType?: string) => {
    const item: ContentItem = {
      id: crypto.randomUUID(),
      type: 'archive-node',
      text,
      metadata: {
        source: {
          type: sourceType ?? 'archive',
          path: ['Curator Result'],
          nodeId,
        },
        wordCount: text.split(/\s+/).filter(Boolean).length,
      },
    };
    try {
      await addToStack(item);
      addToast('Added to stack', 'success');
    } catch (err) {
      log.warn('Failed to add to stack:', err);
      addToast('Failed to add to stack', 'warning');
    }
  }, [addToStack, addToast]);

  // D4: Add to harvest via IPC — finds or creates a harvest for the active project
  const handleAddToHarvest = useCallback(async (harvestItem: { content: string; source?: string }) => {
    if (!window.electronHarvest) {
      addToast('Harvest requires the Electron app', 'warning');
      return;
    }
    if (!activeProject) {
      addToast('No active project — add cards to the stack first', 'info');
      return;
    }
    try {
      // Find existing harvests for this project, or create one
      let harvests = await window.electronHarvest.listHarvests(activeProject.id);
      let harvestId: string;
      if (harvests.length > 0) {
        harvestId = harvests[0].id;
      } else {
        const newHarvest = await window.electronHarvest.createHarvest(activeProject.id, 'Curator Finds');
        harvestId = newHarvest.id;
      }
      await window.electronHarvest.addItemsBatch(harvestId, [harvestItem]);
      addToast('Added to harvest', 'success');
    } catch (err) {
      log.warn('Failed to add to harvest:', err);
      addToast('Failed to add to harvest', 'warning');
    }
  }, [addToast, activeProject]);

  // Handle clicking a saved search in the sidebar
  const handleSavedSearchClick = useCallback((search: SavedSearch) => {
    const fb: FindBuffer = {
      id: search.id,
      query: search.query,
      anchorPassage: search.anchor,
      results: [], // Will be re-searched by CuratorView
      timestamp: search.timestamp,
      savedBufferId: search.id,
    };
    setSidebarFindBuffer(fb);
    // Reset session state so CuratorView picks up the new find buffer
    sessionStateRef.current = undefined;
  }, []);

  // Callback when CuratorView auto-saves a new search
  const handleSearchSaved = useCallback((_fb: FindBuffer) => {
    // Re-fetch sidebar list
    if (!activeProject || !window.electronBuffer?.listByProject) return;
    window.electronBuffer.listByProject(activeProject.id)
      .then((buffers: Array<{ id: string; origin?: { metadata?: Record<string, unknown> }; createdAt?: number }>) => {
        const searches: SavedSearch[] = [];
        for (const buf of buffers) {
          const meta = buf.origin?.metadata;
          if (meta?.curatorSearch) {
            searches.push({
              id: buf.id,
              query: (meta.curatorQuery as string) ?? '',
              resultCount: (meta.resultCount as number) ?? 0,
              anchor: (meta.curatorAnchor as string) ?? '',
              timestamp: (meta.searchTimestamp as number) ?? buf.createdAt ?? 0,
            });
          }
        }
        searches.sort((a, b) => b.timestamp - a.timestamp);
        setSavedSearches(searches);
      })
      .catch(() => {});
  }, [activeProject]);

  const handleNewSearch = useCallback(() => {
    setSidebarFindBuffer(undefined);
    sessionStateRef.current = undefined;
  }, []);

  const hasSidebar = !!activeProject && savedSearches.length > 0;

  return (
    <div className="workspace-curator__overlay" onClick={(e) => {
      if (e.target === e.currentTarget) onClose();
    }} role="presentation">
      <div className={`workspace-curator__modal${hasSidebar ? ' workspace-curator__modal--with-sidebar' : ''}`}>
        <div className="workspace-curator__header">
          <h2 className="workspace-curator__title">Find in Archive</h2>
          <button
            className="workspace-curator__close"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        </div>
        <div className={`workspace-curator__body${hasSidebar ? ' workspace-curator__body--row' : ''}`}>
          {/* Saved searches sidebar */}
          {hasSidebar && (
            <div className="workspace-curator__sidebar">
              <button
                className="workspace-curator__new-search"
                onClick={handleNewSearch}
              >
                + New Search
              </button>
              {savedSearches.map((search) => (
                <button
                  key={search.id}
                  className={`workspace-curator__sidebar-item${sidebarFindBuffer?.savedBufferId === search.id ? ' workspace-curator__sidebar-item--active' : ''}`}
                  onClick={() => handleSavedSearchClick(search)}
                  title={search.query}
                >
                  <span className="workspace-curator__sidebar-query">
                    {search.query.slice(0, 40)}{search.query.length > 40 ? '...' : ''}
                  </span>
                  <span className="workspace-curator__sidebar-meta">
                    {search.resultCount} results · {formatRelativeTime(search.timestamp)}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Main CuratorView */}
          <div className="workspace-curator__main">
            <CuratorView
              key={sidebarFindBuffer?.id ?? 'default'}
              onOpenNode={(nodeId) => {
                if (window.electronArchive) {
                  window.electronArchive.query('getNode', [nodeId])
                    .then((node: unknown) => {
                      const n = node as { text?: string; sourceType?: string } | null;
                      if (n?.text) handleAddToStack(nodeId, n.text, n.sourceType);
                    })
                    .catch((err: unknown) => log.warn('Failed to load node:', err));
                }
              }}
              onSelectContent={async (nodeId) => {
                if (window.electronArchive) {
                  try {
                    const node = await window.electronArchive.query('getNode', [nodeId]) as { text?: string; sourceType?: string } | null;
                    if (node?.text) {
                      await handleAddToStack(nodeId, node.text, node.sourceType);
                    }
                  } catch (err) {
                    log.warn('Failed to load node:', err);
                  }
                }
              }}
              onAddToStack={handleAddToStack}
              onAddToHarvest={handleAddToHarvest}
              initialQuery={sidebarFindBuffer ? null : initialQuery}
              initialFindBuffer={sidebarFindBuffer}
              sessionStateRef={sessionStateRef}
              activeProjectId={activeProject?.id}
              onSearchSaved={handleSearchSaved}
            />
          </div>
        </div>
        {initialQuery && !sidebarFindBuffer && (
          <div className="workspace-curator__prefill">
            <span className="workspace-curator__prefill-label">Searching for:</span>
            <span className="workspace-curator__prefill-text">{initialQuery.slice(0, 100)}{initialQuery.length > 100 ? '...' : ''}</span>
          </div>
        )}
      </div>
    </div>
  );
}

/** Format a timestamp as a relative time string */
function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(timestamp).toLocaleDateString();
}
